/** Sprint 5-b PWA · 行事曆主頁（mobile · 近期事件列表 + 語音建立 FAB + 進會議室）。 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import EventIcon from '@mui/icons-material/Event';

import { useMessage } from '../../../contexts/MessageContext';
import { formatLocal } from '../../../utils/dateHelper';
import { getEvents } from '../../../services/eeMyCalendarService';
import { EeMyCalendar } from '../../../types/eeMyCalendar';

const PWACalendarPage: React.FC = () => {
  const nav = useNavigate();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`pwaCal.${k}`, d);
  const { showApiError } = useMessage();
  const [events, setEvents] = useState<EeMyCalendar[]>([]);

  const load = useCallback(async () => {
    const now = new Date();
    const end = new Date(now.getTime() + 30 * 864e5);
    try {
      const list = await getEvents({ start: now.toISOString(), end: end.toISOString(), expand: true });
      setEvents([...list].sort((a, b) => (a.dtstart || '').localeCompare(b.dtstart || '')));
    } catch (err) { showApiError(err); }
  }, [showApiError]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ my: 1 }}>📅 {tt('title', '我的行事曆')}</Typography>
      <Typography variant="caption" color="text.secondary">{tt('upcoming', '近 30 天')}</Typography>

      <Stack spacing={1} sx={{ mt: 1 }}>
        {events.length === 0 && <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary">{tt('empty', '近期沒有行程，點右下麥克風語音建立。')}</Typography>
        </Paper>}
        {events.map((e) => {
          // H.3：判斷改看 is_meeting（不看 event_type）；會議點擊進公司會議室，秘密會議加鎖頭
          const isMtg = !!e.is_meeting;
          const secret = isMtg && e.public_meeting === false;
          const goRoom = isMtg && e.office_meeting_id
            ? () => nav(`/ee_pwa_office_meetings/${e.office_meeting_id}`)
            : undefined;
          return (
            <Paper key={`${e.id}-${e.dtstart}`} variant="outlined" sx={{ p: 1.5, cursor: goRoom ? 'pointer' : 'default' }}
              onClick={goRoom}>
              <Stack direction="row" alignItems="center" spacing={1}>
                {isMtg ? <MeetingRoomIcon color="primary" /> : <EventIcon color="action" />}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body1" noWrap>{secret ? '🔒 ' : ''}{e.summary}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {e.dtstart ? formatLocal(e.dtstart) : ''}{e.location ? ` · ${e.location}` : ''}</Typography>
                </Box>
                {isMtg && <Chip size="small" color={secret ? 'default' : 'primary'}
                  label={secret ? tt('secret', '秘密') : tt('room', '會議室')} />}
              </Stack>
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};

export default PWACalendarPage;
